import { useState, useCallback, useMemo, useRef, type DragEvent } from "react";
import { useSearchParams } from "react-router";
import { Button, Modal, EmptyState, useToast } from "@/components/ds";
import { CalendarIcon } from "@/icons/CalendarIcon";
import { ChevronLeftIcon } from "@/icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@/icons/ChevronRightIcon";
import { PlusIcon } from "@/icons/PlusIcon";
import { CheckCircleIcon } from "@/icons/CheckCircleIcon";
import { UsersIcon } from "@/icons/UsersIcon";
import { ShiftChip } from "./ShiftChip";
import { ShiftForm } from "./ShiftForm";
import { ValidationError } from "./errors";
import { FillWeek } from "./FillWeek";
import { TeamPanel } from "./TeamPanel";
import type { Shift, ClashInfo } from "@/domain/shifts/types";
import styles from "./RotaGrid.module.css";

interface Site {
  readonly id: string;
  readonly name: string;
}

interface Cleaner {
  readonly membershipId: string;
  readonly name: string;
  readonly email: string;
  readonly pending: boolean;
}

interface RotaGridProps {
  readonly shifts: Shift[];
  readonly sites: readonly Site[];
  readonly cleaners: readonly Cleaner[];
  readonly weekStart: string;
  readonly draftCount: number;
  readonly onRefresh: () => void;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return String(d.getDate());
}

function formatWeekRange(weekStart: string): string {
  const start = new Date(weekStart + "T12:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const startMonth = start.toLocaleString("en-GB", { month: "short" });
  const endMonth = end.toLocaleString("en-GB", { month: "short" });
  const year = start.getFullYear();

  if (startMonth === endMonth) {
    return `${start.getDate()} - ${end.getDate()} ${startMonth} ${year}`;
  }
  return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth} ${year}`;
}

function getCurrentWeekMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

function timesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  return startA < endB && startB < endA;
}

interface ActiveCell {
  readonly siteId: string;
  readonly date: string;
  readonly shift?: Shift;
}

function CellWithForm({
  site,
  date,
  cellShifts,
  isToday,
  activeCell,
  setActiveCell,
  cleaners,
  existingShiftsForClash,
  clashMap,
  highlightedCleaner,
  handleCreateShift,
  handleUpdateShift,
  handleDeleteShift,
  handleMoveShift,
  draggingShiftId,
  onChipDragStart,
  onChipDragEnd,
  closeForm,
}: {
  site: Site;
  date: string;
  cellShifts: Shift[];
  isToday: boolean;
  activeCell: ActiveCell | null;
  setActiveCell: (cell: ActiveCell | null) => void;
  cleaners: readonly Cleaner[];
  existingShiftsForClash: readonly { membershipId: string; startTime: string; endTime: string; siteId: string }[];
  clashMap: Map<string, boolean>;
  highlightedCleaner: string | null;
  handleCreateShift: (data: { siteId: string; membershipId: string; date: string; startTime: string; endTime: string }) => Promise<{ clashes?: readonly ClashInfo[] }>;
  handleUpdateShift: (shiftId: string, data: { membershipId: string; startTime: string; endTime: string }) => Promise<{ clashes?: readonly ClashInfo[] }>;
  handleDeleteShift: (shiftId: string) => Promise<void>;
  handleMoveShift: (shiftId: string, siteId: string, date: string) => Promise<void>;
  draggingShiftId: string | null;
  onChipDragStart: (shiftId: string) => void;
  onChipDragEnd: () => void;
  closeForm: () => void;
}) {
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const chipRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);
  const isActive = activeCell?.siteId === site.id && activeCell?.date === date;
  const isDragSource = draggingShiftId !== null && cellShifts.some((s) => s.id === draggingShiftId);
  const showGhost = dragOver && !isDragSource;
  const isCreating = isActive && !activeCell?.shift;
  const editingShiftId = isActive && activeCell?.shift ? activeCell.shift.id : null;

  const onDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOver(true);
  }, []);

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  }, []);

  const onDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    const shiftId = e.dataTransfer.getData("text/plain");
    if (shiftId) {
      handleMoveShift(shiftId, site.id, date);
    }
  }, [handleMoveShift, site.id, date]);

  return (
    <td className={isToday ? styles.todayCell : undefined}>
      <div
        className={styles.cellContent}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {cellShifts.map((shift) => (
          <ShiftChip
            key={shift.id}
            shiftId={shift.id}
            ref={(el) => {
              if (el) chipRefs.current.set(shift.id, el);
              else chipRefs.current.delete(shift.id);
            }}
            startTime={shift.startTime}
            endTime={shift.endTime}
            cleanerName={shift.cleanerName}
            membershipId={shift.membershipId as string}
            status={shift.status}
            hasClash={clashMap.has(shift.id)}
            highlighted={highlightedCleaner === (shift.membershipId as string)}
            dimmed={highlightedCleaner !== null && highlightedCleaner !== (shift.membershipId as string)}
            active={editingShiftId === shift.id}
            onClick={() => setActiveCell({ siteId: site.id, date, shift })}
            onDragStart={onChipDragStart}
            onDragEnd={onChipDragEnd}
          />
        ))}
        {showGhost && <div className={styles.dropGhost} />}
        <button
          ref={addButtonRef}
          type="button"
          className={`${styles.addButton} ${isCreating ? styles.addButtonActive : ""}`}
          aria-label="Add shift"
          onClick={() => setActiveCell({ siteId: site.id, date })}
        >
          <PlusIcon width={12} height={12} />
        </button>

        {isCreating && (
          <ShiftForm
            anchorRef={addButtonRef}
            siteId={site.id}
            date={date}
            cleaners={cleaners}
            existingShifts={existingShiftsForClash}
            onSave={handleCreateShift}
            onUpdate={handleUpdateShift}
            onDelete={handleDeleteShift}
            onClose={closeForm}
          />
        )}
        {editingShiftId && (() => {
          const shift = activeCell!.shift!;
          const chipEl = chipRefs.current.get(editingShiftId);
          const anchorRef = { current: chipEl ?? null };
          return (
            <ShiftForm
              anchorRef={anchorRef}
              siteId={site.id}
              date={date}
              cleaners={cleaners}
              existingShifts={existingShiftsForClash}
              shift={{
                id: shift.id,
                membershipId: shift.membershipId as string,
                startTime: shift.startTime,
                endTime: shift.endTime,
                status: shift.status,
              }}
              onSave={handleCreateShift}
              onUpdate={handleUpdateShift}
              onDelete={handleDeleteShift}
              onClose={closeForm}
            />
          );
        })()}
      </div>
    </td>
  );
}

export function RotaGrid({
  shifts,
  sites,
  cleaners,
  weekStart,
  draftCount,
  onRefresh,
}: RotaGridProps) {
  const [, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [fillWeekSite, setFillWeekSite] = useState<Site | null>(null);
  const [highlightedCleaner, setHighlightedCleaner] = useState<string | null>(null);
  const [pinnedCleaner, setPinnedCleaner] = useState<string | null>(null);
  const [draggingShiftId, setDraggingShiftId] = useState<string | null>(null);
  const [showTeamPanel, setShowTeamPanel] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<{ shiftId: string; cleanerName: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const currentWeekMonday = getCurrentWeekMonday();

  const effectiveHighlight = pinnedCleaner ?? highlightedCleaner;

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const shiftsBySiteAndDate = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const shift of shifts) {
      const key = `${shift.siteId}:${shift.date}`;
      const list = map.get(key) ?? [];
      list.push(shift);
      map.set(key, list);
    }
    return map;
  }, [shifts]);

  const clashMap = useMemo(() => {
    const map = new Map<string, boolean>();
    const byCleanerDate = new Map<string, Shift[]>();

    for (const shift of shifts) {
      const key = `${shift.membershipId}:${shift.date}`;
      const list = byCleanerDate.get(key) ?? [];
      list.push(shift);
      byCleanerDate.set(key, list);
    }

    for (const group of byCleanerDate.values()) {
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          if (timesOverlap(group[i].startTime, group[i].endTime, group[j].startTime, group[j].endTime)) {
            map.set(group[i].id, true);
            map.set(group[j].id, true);
          }
        }
      }
    }

    return map;
  }, [shifts]);

  const allSitesForGrid = sites;

  const navigateWeek = useCallback((direction: number) => {
    const newWeek = addDays(weekStart, direction * 7);
    setSearchParams({ week: newWeek });
  }, [weekStart, setSearchParams]);

  const goToToday = useCallback(() => {
    setSearchParams({ week: currentWeekMonday });
  }, [currentWeekMonday, setSearchParams]);

  const handleCreateShift = useCallback(async (data: {
    siteId: string;
    membershipId: string;
    date: string;
    startTime: string;
    endTime: string;
  }): Promise<{ clashes?: readonly ClashInfo[] }> => {
    const response = await fetch("/api/shifts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!result.success) {
      if (result.error?.fields) throw new ValidationError(result.error.fields);
      toast("error", result.error?.message ?? "Something went wrong");
      throw new Error(result.error?.message);
    }
    onRefresh();
    return { clashes: result.data.clashes };
  }, [onRefresh, toast]);

  const handleUpdateShift = useCallback(async (shiftId: string, data: {
    membershipId: string;
    startTime: string;
    endTime: string;
  }): Promise<{ clashes?: readonly ClashInfo[] }> => {
    const wasPublished = shifts.find((s) => s.id === shiftId)?.status === "published";
    const response = await fetch(`/api/shifts/${shiftId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (!result.success) {
      if (result.error?.fields) throw new ValidationError(result.error.fields);
      toast("error", result.error?.message ?? "Something went wrong");
      throw new Error(result.error?.message);
    }
    if (wasPublished) {
      toast("success", "Saved. Your cleaner sees the update now.");
    } else {
      toast("success", "Saved");
    }
    onRefresh();
    return { clashes: result.data.clashes };
  }, [shifts, onRefresh, toast]);

  const performDeleteShift = useCallback(async (shiftId: string): Promise<void> => {
    const response = await fetch(`/api/shifts/${shiftId}`, { method: "DELETE" });
    const result = await response.json();
    if (!result.success) {
      toast("error", result.error?.message ?? "Something went wrong");
      throw new Error(result.error?.message);
    }
    onRefresh();
  }, [onRefresh, toast]);

  const handleDeleteShift = useCallback(async (shiftId: string): Promise<void> => {
    const target = shifts.find((s) => s.id === shiftId);
    if (target?.status === "published") {
      setPendingDelete({ shiftId, cleanerName: target.cleanerName });
      return;
    }
    await performDeleteShift(shiftId);
  }, [shifts, performDeleteShift]);

  const confirmPendingDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await performDeleteShift(pendingDelete.shiftId);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, performDeleteShift]);

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    try {
      const response = await fetch("/api/shifts/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ weekStart }),
      });
      const result = await response.json();
      if (!result.success) {
        toast("error", result.error?.message ?? "Something went wrong");
        return;
      }
      toast("success", "Published");
      setShowPublishModal(false);
      onRefresh();
    } finally {
      setPublishing(false);
    }
  }, [weekStart, onRefresh, toast]);

  const handleMoveShift = useCallback(async (shiftId: string, targetSiteId: string, targetDate: string) => {
    const wasPublished = shifts.find((s) => s.id === shiftId)?.status === "published";
    const response = await fetch(`/api/shifts/${shiftId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ siteId: targetSiteId, date: targetDate }),
    });
    const result = await response.json();
    if (!result.success) {
      toast("error", result.error?.message ?? "Something went wrong");
      return;
    }
    if (wasPublished) {
      toast("info", "Moved. Your cleaner will see the update right away.");
    }
    onRefresh();
  }, [shifts, onRefresh, toast]);

  const handleCopyWeek = useCallback(async () => {
    setCopying(true);
    try {
      const sourceWeek = addDays(weekStart, -7);
      const response = await fetch("/api/shifts/copy-week", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceWeek, targetWeek: weekStart }),
      });
      const result = await response.json();
      if (!result.success) {
        toast("error", result.error?.fields?.sourceWeek ?? "Something went wrong");
        return;
      }
      const count = result.data.shifts.length;
      const skipped = result.data.skippedCount;
      if (skipped > 0) {
        toast("success", `Copied ${count} shifts (${skipped} skipped)`);
      } else {
        toast("success", `Copied ${count} shifts`);
      }
      onRefresh();
    } finally {
      setCopying(false);
    }
  }, [weekStart, onRefresh, toast]);

  const closeForm = useCallback(() => setActiveCell(null), []);
  const onChipDragStart = useCallback((id: string) => setDraggingShiftId(id), []);
  const onChipDragEnd = useCallback(() => setDraggingShiftId(null), []);

  const handlePinCleaner = useCallback((membershipId: string) => {
    setPinnedCleaner((prev) => prev === membershipId ? null : membershipId);
    setHighlightedCleaner(null);
  }, []);

  const handleHighlightCleaner = useCallback((membershipId: string | null) => {
    if (!pinnedCleaner) {
      setHighlightedCleaner(membershipId);
    }
  }, [pinnedCleaner]);

  const existingShiftsForClash = useMemo(() => {
    if (!activeCell) return [];
    return shifts.filter(
      (s) => s.date === activeCell.date && (!activeCell.shift || s.id !== activeCell.shift.id),
    );
  }, [shifts, activeCell]);

  if (sites.length === 0) {
    return (
      <EmptyState
        icon={<CalendarIcon width={32} height={32} />}
        title="No sites yet"
        description="Add a cleaning site first, then come back to build your rota."
        action={<Button href="/sites">Add a site</Button>}
      />
    );
  }

  const hasNoShifts = shifts.length === 0;

  return (
    <div>
      <div className={styles.weekHeader}>
        <div className={styles.weekNav}>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => navigateWeek(-1)}
            aria-label="Previous week"
          >
            <ChevronLeftIcon width={16} height={16} />
          </button>
          <span className={styles.weekLabel}>{formatWeekRange(weekStart)}</span>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => navigateWeek(1)}
            aria-label="Next week"
          >
            <ChevronRightIcon width={16} height={16} />
          </button>
          {weekStart !== currentWeekMonday && (
            <button type="button" className={styles.todayButton} onClick={goToToday}>
              Today
            </button>
          )}
        </div>

        <div className={styles.weekActions}>
          {hasNoShifts && (
            <Button size="sm" variant="secondary" onClick={handleCopyWeek} disabled={copying}>
              {copying ? "Copying..." : "Copy from last week"}
            </Button>
          )}
          {draftCount > 0 ? (
            <Button size="sm" variant="accent" onClick={() => setShowPublishModal(true)}>
              Publish {draftCount} {draftCount === 1 ? "shift" : "shifts"}
            </Button>
          ) : shifts.length > 0 ? (
            <span className={styles.publishedBadge}>
              <CheckCircleIcon width={14} height={14} />
              All published
            </span>
          ) : null}
          {cleaners.length > 0 && !showTeamPanel && (
            <button
              type="button"
              className={styles.navButton}
              onClick={() => setShowTeamPanel(true)}
              aria-label="Show team panel"
              title="Show team"
            >
              <UsersIcon width={16} height={16} />
            </button>
          )}
        </div>
      </div>

      <div className={styles.layout}>
        <div className={styles.gridWrapper}>
          <table className={styles.grid} role="grid">
            <thead>
              <tr>
                <th className={styles.siteColumn}>Site</th>
                {weekDates.map((date, i) => (
                  <th key={date} className={date === today ? styles.todayColumn : undefined}>
                    {DAY_NAMES[i]} {formatDateShort(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allSitesForGrid.map((site) => (
                <tr key={site.id} role="row">
                  <td className={styles.siteNameCell}>
                    <div className={styles.siteNameInner}>
                      <span className={styles.siteLabel} title={site.name}>{site.name}</span>
                      <button
                        type="button"
                        className={styles.fillButton}
                        aria-label={`Fill week for ${site.name}`}
                        title="Fill week"
                        onClick={() => setFillWeekSite(site)}
                      >
                        <CalendarIcon width={12} height={12} />
                      </button>
                    </div>
                  </td>
                  {weekDates.map((date) => {
                    const cellShifts = shiftsBySiteAndDate.get(`${site.id}:${date}`) ?? [];
                    const isToday = date === today;

                    return (
                      <CellWithForm
                        key={date}
                        site={site}
                        date={date}
                        cellShifts={cellShifts}
                        isToday={isToday}
                        activeCell={activeCell}
                        setActiveCell={setActiveCell}
                        cleaners={cleaners}
                        existingShiftsForClash={existingShiftsForClash}
                        clashMap={clashMap}
                        highlightedCleaner={effectiveHighlight}
                        handleCreateShift={handleCreateShift}
                        handleUpdateShift={handleUpdateShift}
                        handleDeleteShift={handleDeleteShift}
                        handleMoveShift={handleMoveShift}
                        draggingShiftId={draggingShiftId}
                        onChipDragStart={onChipDragStart}
                        onChipDragEnd={onChipDragEnd}
                        closeForm={closeForm}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showTeamPanel && (
          <TeamPanel
            cleaners={cleaners}
            shifts={shifts}
            sites={sites}
            weekStart={weekStart}
            highlightedCleaner={effectiveHighlight}
            pinnedCleaner={pinnedCleaner}
            onHighlight={handleHighlightCleaner}
            onPin={handlePinCleaner}
            onClose={() => setShowTeamPanel(false)}
          />
        )}
      </div>

      {fillWeekSite && (
        <FillWeek
          open={true}
          onClose={() => { setFillWeekSite(null); onRefresh(); }}
          siteName={fillWeekSite.name}
          siteId={fillWeekSite.id}
          weekDates={weekDates}
          cleaners={cleaners}
          onCreateShift={handleCreateShift}
        />
      )}

      <Modal
        open={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        title="Publish this week's rota"
        footer={
          <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
            <Button size="sm" variant="secondary" onClick={() => setShowPublishModal(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="accent" onClick={handlePublish} disabled={publishing}>
              {publishing ? "Publishing..." : "Confirm"}
            </Button>
          </div>
        }
        size="sm"
      >
        <p style={{ fontSize: "var(--type-small-size)", color: "var(--color-text-secondary)", lineHeight: "var(--type-small-line-height)" }}>
          This will publish {draftCount} {draftCount === 1 ? "shift" : "shifts"}. Your team will see their new rota on their phones right away.
        </p>
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
        title="Remove this shift?"
        footer={
          <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
            <Button size="sm" variant="secondary" onClick={() => setPendingDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={confirmPendingDelete} disabled={deleting}>
              {deleting ? "Removing..." : "Remove shift"}
            </Button>
          </div>
        }
        size="sm"
      >
        <p style={{ fontSize: "var(--type-small-size)", color: "var(--color-text-secondary)", lineHeight: "var(--type-small-line-height)" }}>
          {pendingDelete?.cleanerName ?? "Your cleaner"} will no longer see this shift on their schedule.
        </p>
      </Modal>
    </div>
  );
}
